import { Alert, Button, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GranularDataCard from 'src/App/HOC/Cards/GranularDataCard';
import { COLOR_PRIMARY_1, COLOR_WARNING } from 'src/colors';
import { COLOR_E, COLOR_PRIMARY, exchangeRates, sampleMarket } from 'src/const';
import { SearchProp } from 'src/interfaces/iproptypes';
import { User } from 'src/interfaces/iuser';
import { getDistrictInfo } from 'src/store/Dashboard/actions';

const GranularDataDashboard = ({
  user,
  search,
  isUserAllowed,
  currency,
}: {
  user: User;
  search: SearchProp;
  isUserAllowed: boolean;
  currency: string;
}) => {
  // const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dailyData, setDailyData] = useState<any>(null);
  const [weeklyData, setWeeklyData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>(null);
  const [isMarketLocked, setIsMarketLocked] = useState<boolean>(!isUserAllowed);
  const [isDisabled, setisDisabled] = useState<boolean>(false);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState<boolean>(false);

  const dispatch = useDispatch();
  const historicalData = useSelector(state => (state as any).dashboard.historicalData);
  const isLoading = useSelector(state => (state as any).dashboard.historicalData.loading);

  const params = window.location.pathname.split('/').splice(2, 3); // ['US', 'CA', 'Malibu']
  const country = params[0];
  const city = params[1];
  const marketName = params[2];
  const formattedMarketName = marketName?.replace(/%20/g, ' ').toLowerCase();

  useEffect(() => {
    if (formattedMarketName.toLowerCase() === sampleMarket.toLowerCase()) {
      dispatch(getDistrictInfo(formattedMarketName, city, country));
    }
  }, []);

  useEffect(() => {
    setIsMarketLocked(!isUserAllowed);
  }, [isUserAllowed]);

  useEffect(() => {
    if (!historicalData.loading && historicalData.data) {
      setDailyData(historicalData.data.message.daily_past_pricings);
      setWeeklyData(processWeeklyData(historicalData.data.message.weekly_past_pricings));
      setMonthlyData(processMonthlyData(historicalData.data.message.monthly_past_pricings));
    }
  }, [historicalData.data]);

  const processMonthlyData = monthlyData => {
    const tempMonthlyData: any = {};
    for (const date in monthlyData) {
      tempMonthlyData[date + '-01'] = {
        ...monthlyData[date],
      };
    }
    return tempMonthlyData;
  };

  const processWeeklyData: any = weeklyData => {
    const tempWeeklyData: any = {};
    for (const date in weeklyData) {
      tempWeeklyData[date.split('_')[0]] = {
        ...weeklyData[date],
        endDate: date.split('_')[1],
      };
    }
    return tempWeeklyData;
  };

  useEffect(() => {
    user.email !== 'Trial' && setIsUserInfoLoaded(true);
  }, [user]);

  useEffect(() => {
    if (isDisabled) {
      window.scrollTo(0, 0);
    }
  }, [isDisabled]);

  // console.log('isUserAllowed: ', isUserAllowed);

  if (isLoading && !historicalData.data) {
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (
    !isLoading &&
    (!historicalData.data ||
      !historicalData.data?.message ||
      Object.entries(historicalData.data.message?.monthly_past_pricings).length === 0)
  ) {
    return <HistoricalDataFetchingErrorScreen />;
  }

  return (
    <>
      <Grid
        container
        direction="column"
        spacing={2}
        style={{ position: 'relative', top: 0, left: 0 }}
      >
        <Grid container direction={'row'} sx={{ width: '100%' }}>
          <Grid item xs={12} sx={{ marginTop: '20px' }}>
            {isDisabled && (
              <Alert
                severity="warning"
                sx={{
                  border: `2px solid ${COLOR_WARNING}`,
                  fontWeight: 'bold',
                  boxShadow: '0 4px 8px #ccc',
                }}
              >
                Showing the demo market: The City of Brighton and Hove
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', marginBottom: '50px' }}>
            {dailyData && (
              <GranularDataCard
                metric={'revenue'}
                name={'Revenue'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: exchangeRates[currency].symbol, isCurrency: true }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                showNoFilter={true}
                isApiFetching={isLoading}
                isDisabled={isDisabled}
              />
            )}
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', marginBottom: '50px' }}>
            {dailyData && (
              <GranularDataCard
                metric={'occupancy_rate'}
                name={'Occupancy Rate'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: '%', isCurrency: false }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                isApiFetching={isLoading}
                isDisabled={isDisabled}
              />
            )}
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', marginBottom: '50px' }}>
            {dailyData && (
              <GranularDataCard
                metric={'daily_rate'}
                name={'Average Daily Rate'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: exchangeRates[currency].symbol, isCurrency: true }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                showNoFilter={true}
                isApiFetching={isLoading}
                isDisabled={isDisabled}
              />
            )}
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', marginBottom: '50px' }}>
            {dailyData && (
              <GranularDataCard
                metric={'booking_lead'}
                name={'Booking Lead'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: 'Days', isCurrency: false }}
                isMarketLocked={isMarketLocked}
                showNoFilter={true}
                currency={currency}
                isApiFetching={isLoading}
                isDisabled={isDisabled}
              />
            )}
          </Grid>
          {isMarketLocked && (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100vh',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  flexDirection: 'column',
                  color: COLOR_E,
                }}
              >
                <Button
                  onClick={() => {
                    setIsMarketLocked(false);
                    setisDisabled(true);
                  }}
                  style={{ all: 'unset', cursor: 'pointer' }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    View Demo Data
                  </Typography>
                </Button>
              </div>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const HistoricalDataFetchingErrorScreen = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          background: '#fff',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 4px 10px -2px #ccc',
          height: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
        }}
      >
        <Typography variant="h3">Something went wrong</Typography>
        <Typography variant="h5">
          Please{' '}
          <span
            style={{
              color: COLOR_PRIMARY_1,
              cursor: 'pointer',
              transition: 'color 0.25s ease',
              textDecoration: 'wavy underline',
            }}
            onClick={() => window.location.reload()}
          >
            try again
          </span>
        </Typography>
        <Typography
          variant="body1"
          style={{
            position: 'relative',
            top: '20px',
            color: '#aaa',
          }}
        >
          ** Please make sure your internet connection is available.
        </Typography>
      </div>
    </div>
  );
};

export default GranularDataDashboard;
